import { FabricEditor, FabricEditorCanvas, FabricEditorProvider } from 'modules/fabric-editor';
import { applyLayoutBackgrounds, isRenderImageError, substitutePlaceholders } from 'modules/manager/domain/render';
import mediaManagerRepository, { StageRenderedImageInput } from 'modules/manager/domain/repositories/media-manager-repository';
import { fetchTemplateOnly, resetEditor, selectActiveTemplate, selectError } from 'modules/manager/store';
import { useGetStandingsRenderContextQuery } from 'modules/manager/store/standings-render-api';
import { TemplateType } from 'modules/manager/domain/template-type';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { RENDER_PLUGINS, StatusOverlay, collectBgUrls, loadCanvasJson, nextFrame, parseId } from './render-shared';

const StandingsRenderPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();

  const stageId = parseId(params.get('stageId'));
  const templateId = parseId(params.get('templateId'));
  const bgUrls = useMemo(() => collectBgUrls(params), [params]);

  const activeTemplate = useAppSelector(selectActiveTemplate);
  const loadError = useAppSelector(selectError);
  const ctxQuery = useGetStandingsRenderContextQuery(stageId !== null ? { stageId } : skipToken);

  const [editor, setEditor] = useState<FabricEditor | null>(null);
  const [status, setStatus] = useState<'loading' | 'rendering' | 'done' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const orchestrated = useRef(false);

  useEffect(() => {
    if (templateId === null) {
      setStatus('error');
      setErrorMessage('Missing templateId in URL');
      return;
    }
    dispatch(fetchTemplateOnly({ templateId }));
    return () => {
      dispatch(resetEditor());
    };
  }, [dispatch, templateId]);

  useEffect(() => {
    if (stageId === null) {
      setStatus('error');
      setErrorMessage('Missing stageId in URL');
    }
  }, [stageId]);

  useEffect(() => {
    if (orchestrated.current) return;
    if (!editor || !activeTemplate || !ctxQuery.data || stageId === null) return;
    if (loadError) {
      console.error(loadError);
      setStatus('error');
      setErrorMessage(loadError);
      return;
    }
    if (activeTemplate.templateType !== TemplateType.TOURNAMENT_TABLE) {
      setStatus('error');
      setErrorMessage('Template is not a Турнирная таблица (TOURNAMENT_TABLE) template');
      return;
    }
    orchestrated.current = true;
    setStatus('rendering');

    (async () => {
      try {
        const images: StageRenderedImageInput[] = [];
        for (const target of ctxQuery.data!.targets) {
          await loadCanvasJson(editor, activeTemplate.version, activeTemplate.objects ?? []);
          if (bgUrls.length > 0) await applyLayoutBackgrounds(editor, bgUrls);
          await substitutePlaceholders(editor, target, { templateType: activeTemplate.templateType });
          editor.canvas.requestRenderAll();
          await nextFrame();
          const dataUrl = editor.canvas.toDataURL({ format: 'png', multiplier: 1 });
          images.push({
            groupKey: target.group ? String(target.group.id) : '',
            groupId: target.group?.id,
            groupName: target.group?.name,
            dataUrl,
          });
        }
        await mediaManagerRepository.persistStageRenderedImages({
          stageId,
          templateId: activeTemplate.id,
          images,
        });
        setStatus('done');
      } catch (err) {
        console.error(err);
        const userMessage = isRenderImageError(err)
          ? err.userMessage
          : err instanceof Error
            ? err.message
            : String(err);
        setStatus('error');
        setErrorMessage(userMessage);
      }
    })();
  }, [editor, activeTemplate, ctxQuery.data, loadError, stageId, bgUrls]);

  useEffect(() => {
    if (ctxQuery.error) {
      console.error(ctxQuery.error);
      setStatus('error');
      setErrorMessage((ctxQuery.error as { message?: string }).message ?? 'Failed to load standings render context');
    }
  }, [ctxQuery.error]);

  if (!activeTemplate) {
    return <StatusOverlay status={status} message={errorMessage ?? 'Loading template…'} title={statusTitle} />;
  }

  return (
    <>
      <div style={{ position: 'fixed', left: -99999, top: -99999, pointerEvents: 'none' }} aria-hidden>
        <FabricEditorProvider editor={editor}>
          <FabricEditorCanvas
            key={`${activeTemplate.id}-${activeTemplate.width}x${activeTemplate.height}`}
            width={activeTemplate.width}
            height={activeTemplate.height}
            resolution={{ width: activeTemplate.width, height: activeTemplate.height }}
            plugins={RENDER_PLUGINS}
            onReady={setEditor}
          />
        </FabricEditorProvider>
      </div>
      <StatusOverlay status={status} message={errorMessage} title={statusTitle} />
    </>
  );
};

function statusTitle(status: string): string {
  switch (status) {
    case 'loading': return 'Loading template and stage data…';
    case 'rendering': return 'Rendering standings…';
    case 'done': return 'Images saved successfully.';
    case 'error': return 'Render failed';
    default: return status;
  }
}

export default StandingsRenderPage;
